import { useNavigate } from 'react-router-dom';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';

// The glyph drawn in the circle, chosen as company does for the same screens
// (`EmptyStateCompany`). Types left out get CompactEmptyState's page.
const GLYPHS = {
  mission_done: 'badgeCheck',
  mission_in_progress: 'inbox',
  mission_soon: 'inbox',
  mission_search: 'searchX',
  mission_search_dashboard: 'searchX',
  select_mission: 'fileText',
  company: 'searchX',
  contract: 'badgeCheck',
  time_sheet: 'clock',
  time_sheet_to_be_completed: 'clock',
  time_sheet_to_be_checked: 'clock',
  time_sheet_litigation: 'clock',
  time_sheet_checked: 'clock',
  schedule: 'calendarX',
  schedule_missing_periods: 'calendarX',
  payslip: 'fileText',
  invoice: 'receiptText',
  coming_soon: 'hourglass',
  log_vivier: 'inbox',
  mission_invitation: 'searchX',
  avis_clients: 'star',
  identity_validation: 'squareCheck',
  qualification: 'fileText',
  no_profile_assigned: 'inbox',
};

const TITLES = {
  mission_done: 'Aucune mission terminées',
  mission_in_progress: 'Aucune mission en cours',
  mission_soon: 'Aucune mission prochainement',
  mission_search: 'Pas de missions disponibles',
  company: 'Aucune entreprise trouvée',
  contract: 'Aucun contrat signé disponible',
  time_sheet: 'Aucun relevé d’heure à compléter',
  payslip: 'Aucune fiche de paie',
  coming_soon: 'Fonctionnalité bientôt disponible',
  select_mission: 'Sélectionnez une mission',
  log_vivier: 'Aucune notification de vivier',
  schedule_missing_periods: 'Période manquante',
  schedule: 'Nouvelle fonctionnalité Planning',
  mission_invitation: 'Aucun intérimaire à inviter',
  identity_validation: 'Aucun utilisateur à valider',
  qualification: 'Aucune qualification trouvée',
  avis_clients: 'Aucun avis client',
  time_sheet_to_be_completed: 'Aucun relevé non complété',
  time_sheet_to_be_checked: 'Aucun relevé à vérifier',
  time_sheet_litigation: 'Aucun relevé en litige',
  time_sheet_checked: 'Aucun relevé vérifié',
  no_profile_assigned: 'Aucun profil attribué',
};

const DESCRIPTIONS = {
  mission_done:
    'Oops, vous n’avez pas encore de missions terminées, pour en avoir il faut postuler à une mission !',
  mission_in_progress:
    'Oops, vous n’avez pas encore de missions en cours, pour en avoir il faut postuler à une mission !',
  mission_soon:
    "Oops, vous n'avez pas encore de missions prochainement, pour en avoir, il faut postuler à une mission !",
  mission_search:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  company:
    'Aucune entreprise ne correspond à votre recherche ou à vos filtres. Modifiez vos critères ou ajoutez une nouvelle entreprise.',
  contract:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  time_sheet:
    'Oops, il n’y a pas encore de missions disponibles pour le moment, un peu de patience',
  payslip:
    'Les fiches de paie du mois apparaîtront ici dès qu’elles seront importées. Changez de mois pour en voir d’autres.',
  coming_soon:
    'Vous serez notifié une fois que cette fonctionnalité sera disponible.',
  select_mission: 'Sélectionnez une mission pour exécuter une action.',
  log_vivier:
    'Oops, il n’y a pas encore de notification de viviers pour le moment.',
  schedule_missing_periods:
    'Ajoutez une date de début et de fin pour pouvoir éditer le planning de la mission',
  schedule:
    "Activez la fonctionnalité planning pour un suivi clair et efficace de vos missions d'intérim.",
  mission_invitation: "Pas d'intérimaire à inviter pour le moment",
  identity_validation:
    "Il n'y a pas encore d'utilisateurs en attente de validation d'identité.",
  qualification:
    "Il n'y a pas encore de qualifications disponibles. Créez-en une pour commencer.",
  avis_clients: "Il n'y a pas encore d'avis clients à traiter pour le moment.",
  time_sheet_to_be_completed:
    "Aucun relevé d'heures n'est en attente de complétion par l'intérimaire pour cette période.",
  time_sheet_to_be_checked:
    "Aucun relevé d'heures n'est en attente de vérification pour cette période.",
  time_sheet_litigation:
    "Aucun relevé d'heures n'est en litige pour cette période.",
  time_sheet_checked:
    "Aucun relevé d'heures n'a été vérifié pour cette période.",
  no_profile_assigned: 'Vous devez attribuer un profil à cette mission.',
};

// Types that come with their own button; an `action` prop replaces it.
const NAVIGATIONS = {
  mission_done: { label: 'Rechercher de missions', path: '/searchMission' },
  mission_in_progress: {
    label: 'Rechercher de missions',
    path: '/searchMission',
  },
  mission_soon: { label: 'Rechercher de missions', path: '/searchMission' },
};

/**
 * The empty state of a screen, in the one layout of the site
 * (CompactEmptyState, in the style of company's).
 * `type` picks the glyph, the title, the text and sometimes a button;
 * `title`, `description` and `action` ({ label, onClick }) override them.
 * `compact`: inside a slide-over or a modal.
 */
const EmptyState = ({
  type,
  title: titleOverride,
  description: descriptionOverride,
  action,
  compact,
}) => {
  const navigate = useNavigate();

  const navigation = NAVIGATIONS[type];
  const resolvedAction =
    action ??
    (navigation && {
      label: navigation.label,
      onClick: () => navigate(navigation.path),
    });

  return (
    <CompactEmptyState
      glyph={GLYPHS[type]}
      title={titleOverride ?? TITLES[type]}
      description={descriptionOverride ?? DESCRIPTIONS[type]}
      action={resolvedAction || undefined}
      compact={compact}
    />
  );
};

export default EmptyState;
